import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { LockIcon, MailIcon, UserIcon } from "../../components/icons";
import { OtpBadge, OtpInput, type OtpStatus } from "../../components/OtpInput";
import { authClient } from "../../lib/auth";
import { AuthCheck, AuthField, AuthLayout, AuthSubmit, leaveAuth } from "./AuthShell";

/** Better Auth error codes we expect, in Spanish. */
function authMessage(error: { code?: string; message?: string }, fallback: string) {
  switch (error.code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return "El correo o la contraseña no coinciden.";
    case "USER_ALREADY_EXISTS":
    case "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL":
      return "Ya existe una cuenta con ese correo. Inicia sesión.";
    case "TOO_MANY_ATTEMPTS":
    case "ACCOUNT_LOCKED":
      return error.message || "Demasiados intentos. Prueba de nuevo en unos minutos.";
    case "PASSWORD_TOO_SHORT":
      return "La contraseña debe tener al menos 12 caracteres.";
    default:
      return fallback;
  }
}

/** Where to go after signing in: a same-site path from ?next=, or the inbox. */
function useNext(): string {
  const search = useSearch({ strict: false }) as { next?: unknown };
  const next = typeof search.next === "string" ? search.next : "";
  return next.startsWith("/") && !next.startsWith("//") ? next : "/bandeja";
}

function ErrorText({ children }: { children: string | null }) {
  if (!children) return null;
  return (
    <p className="error-text" role="alert">
      {children}
    </p>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const next = useNext();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsCode, setNeedsCode] = useState(false);
  const [useBackup, setUseBackup] = useState(false);
  const [otp, setOtp] = useState<OtpStatus>("idle");
  // Off by default: a shared or borrowed computer should ask for the code every time.
  const [trustDevice, setTrustDevice] = useState(false);

  async function enter() {
    await leaveAuth();
    navigate({ to: next });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    const { data, error } = await authClient.signIn.email({
      email: String(form.get("email")),
      password: String(form.get("password")),
    });
    setBusy(false);
    if (error) return setError(authMessage(error, "No pudimos iniciar sesión. Intenta de nuevo."));
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) return setNeedsCode(true);
    await enter();
  }

  async function verify(code: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    if (!useBackup) setOtp("verifying");
    const { error } = useBackup
      ? await authClient.twoFactor.verifyBackupCode({ code, trustDevice })
      : await authClient.twoFactor.verifyTotp({ code, trustDevice });
    setBusy(false);
    if (error) {
      setOtp("error");
      return setError(
        authMessage(
          error,
          useBackup ? "Ese código de respaldo no es válido." : "El código no coincide. Prueba con el siguiente.",
        ),
      );
    }
    if (useBackup) return enter();
    // A beat to see the check before the panel opens.
    setOtp("success");
    setTimeout(enter, 650);
  }

  function onCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    verify(String(new FormData(e.currentTarget).get("code")).replace(/\s/g, ""));
  }

  if (needsCode) {
    return (
      <AuthLayout
        icon={useBackup ? undefined : <OtpBadge status={otp} />}
        title={otp === "success" ? "Listo, eres tú" : "Verificación en dos pasos"}
        subtitle={
          useBackup
            ? "Escribe uno de tus códigos de respaldo."
            : "Escribe el código de 6 dígitos de tu app de autenticación."
        }
      >
        <form onSubmit={onCode} key={useBackup ? "backup" : "totp"}>
          {/* Before the code: the sixth digit sends it, so the choice has to come first. */}
          <AuthCheck checked={trustDevice} onChange={setTrustDevice} hint="No lo marques en computadoras compartidas.">
            Confiar en este dispositivo por 30 días
          </AuthCheck>
          {useBackup ? (
            <AuthField
              id="code"
              name="code"
              label="Código de respaldo"
              icon={<LockIcon />}
              autoComplete="one-time-code"
              placeholder="Ej.: a1b2c-d3e4f"
              required
            />
          ) : (
            <div className="auth-field">
              <label htmlFor="code">Código</label>
              <OtpInput
                id="code"
                status={otp}
                onComplete={verify}
                onRetry={() => {
                  setOtp("idle");
                  setError(null);
                }}
              />
            </div>
          )}
          <ErrorText>{error}</ErrorText>
          <AuthSubmit busy={busy} disabled={otp === "success"}>
            {otp === "success" ? "Entrando…" : busy ? "Verificando…" : "Verificar"}
          </AuthSubmit>
        </form>
        <button
          className="auth-link"
          type="button"
          onClick={() => {
            setUseBackup(!useBackup);
            setOtp("idle");
            setError(null);
          }}
        >
          {useBackup ? "Usar la app de autenticación" : "Perdí el teléfono: usar un código de respaldo"}
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Inicia sesión" subtitle="Entra al panel de tu empresa.">
      <form onSubmit={onSubmit}>
        <AuthField
          id="email"
          name="email"
          type="email"
          label="Correo"
          icon={<MailIcon />}
          autoComplete="email"
          placeholder="nombre@empresa.com"
          required
        />
        <AuthField
          id="password"
          name="password"
          type="password"
          label="Contraseña"
          icon={<LockIcon />}
          autoComplete="current-password"
          placeholder="Tu contraseña"
          required
        />
        <Link className="auth-link auth-link-end" to="/recuperar">
          ¿Olvidaste tu contraseña?
        </Link>
        <ErrorText>{error}</ErrorText>
        <AuthSubmit busy={busy}>{busy ? "Entrando…" : "Entrar"}</AuthSubmit>
      </form>
      <p className="auth-alt">
        ¿Primera vez?{" "}
        <Link to="/registro" search={{ next }}>
          Crea tu cuenta
        </Link>
      </p>
    </AuthLayout>
  );
}

/** Sign-up, usually from an invitation link (?next=/invitacion/...). */
export function RegisterPage() {
  const navigate = useNavigate();
  const next = useNext();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const password = String(form.get("password"));
    if (password !== String(form.get("confirm"))) return setError("Las contraseñas no coinciden.");
    setBusy(true);
    setError(null);
    const { error } = await authClient.signUp.email({
      name: String(form.get("name")),
      email: String(form.get("email")),
      password,
    });
    setBusy(false);
    if (error) return setError(authMessage(error, "No pudimos crear la cuenta. Intenta de nuevo."));
    await leaveAuth();
    navigate({ to: next });
  }

  return (
    <AuthLayout title="Crea tu cuenta" subtitle="Después, un administrador de Orbis te asigna a tu empresa.">
      <form onSubmit={onSubmit}>
        <AuthField
          id="name"
          name="name"
          label="Nombre"
          icon={<UserIcon />}
          autoComplete="name"
          placeholder="Ej.: Ana Rodríguez"
          required
          maxLength={80}
        />
        <AuthField
          id="email"
          name="email"
          type="email"
          label="Correo"
          icon={<MailIcon />}
          autoComplete="email"
          placeholder="nombre@empresa.com"
          required
        />
        <AuthField
          id="password"
          name="password"
          type="password"
          label="Contraseña (mínimo 12 caracteres)"
          icon={<LockIcon />}
          autoComplete="new-password"
          placeholder="Mínimo 12 caracteres"
          minLength={12}
          required
        />
        <AuthField
          id="confirm"
          name="confirm"
          type="password"
          label="Repite la contraseña"
          icon={<LockIcon />}
          autoComplete="new-password"
          placeholder="Escríbela otra vez"
          required
        />
        <ErrorText>{error}</ErrorText>
        <p className="auth-consent">
          Al crear tu cuenta aceptas los <Link to="/terminos">términos</Link> y la{" "}
          <Link to="/privacidad">política de privacidad</Link>.
        </p>
        <AuthSubmit busy={busy}>{busy ? "Creando…" : "Crear cuenta"}</AuthSubmit>
      </form>
      <p className="auth-alt">
        ¿Ya tienes cuenta?{" "}
        <Link to="/login" search={{ next }}>
          Inicia sesión
        </Link>
      </p>
    </AuthLayout>
  );
}

/** "¿Olvidaste tu contraseña?": emails a link to choose a new one. */
export function ForgotPasswordPage() {
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get("email")).trim();
    setBusy(true);
    setError(null);
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/restablecer`,
    });
    setBusy(false);
    if (error) return setError(authMessage(error, "No pudimos enviar el enlace. Intenta de nuevo."));
    setSent(email);
  }

  if (sent) {
    return (
      <AuthLayout
        icon={
          <span className="auth-icon" aria-hidden="true">
            <MailIcon />
          </span>
        }
        title="Revisa tu correo"
        subtitle={`Si ${sent} tiene una cuenta en Orbis, te enviamos un enlace.`}
      >
        <p className="auth-alt">El enlace vence en 1 hora. Si no lo ves, revisa la carpeta de spam o promociones.</p>
        <Link className="auth-submit" to="/login">
          Volver a iniciar sesión
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="¿Olvidaste tu contraseña?"
      subtitle="Escribe tu correo y te enviamos un enlace para crear una nueva."
    >
      <form onSubmit={onSubmit}>
        <AuthField
          id="email"
          name="email"
          type="email"
          label="Correo"
          icon={<MailIcon />}
          autoComplete="email"
          placeholder="nombre@empresa.com"
          required
        />
        <ErrorText>{error}</ErrorText>
        <AuthSubmit busy={busy}>{busy ? "Enviando…" : "Enviar enlace"}</AuthSubmit>
      </form>
      <Link className="auth-link" to="/login">
        Volver a iniciar sesión
      </Link>
    </AuthLayout>
  );
}

/** Opened from the email link: choose the new password. */
export function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string; error?: string };
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const token = typeof search.token === "string" ? search.token : null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    const form = new FormData(e.currentTarget);
    const newPassword = String(form.get("password"));
    if (newPassword !== String(form.get("confirm"))) return setError("Las contraseñas no coinciden.");
    setBusy(true);
    setError(null);
    const { error } = await authClient.resetPassword({ newPassword, token });
    setBusy(false);
    if (error)
      return setError(
        error.code === "INVALID_TOKEN"
          ? "El enlace venció o ya se usó. Pide uno nuevo."
          : authMessage(error, "No pudimos cambiar la contraseña. Intenta de nuevo."),
      );
    setDone(true);
  }

  if (!token || search.error) {
    return (
      <AuthLayout title="El enlace no sirve" subtitle="Venció o ya se usó. Los enlaces duran 1 hora y sirven una vez.">
        <Link className="auth-submit" to="/recuperar">
          Pedir un enlace nuevo
        </Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout
        icon={<OtpBadge status="success" />}
        title="Listo, contraseña nueva"
        subtitle="Cerramos todas las sesiones abiertas. Entra con tu contraseña nueva."
      >
        <Link className="auth-submit" to="/login">
          Iniciar sesión
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Crea una contraseña nueva" subtitle="Al guardarla se cierran todas las sesiones abiertas.">
      <form onSubmit={onSubmit}>
        <AuthField
          id="password"
          name="password"
          type="password"
          label="Contraseña nueva (mínimo 12 caracteres)"
          icon={<LockIcon />}
          autoComplete="new-password"
          placeholder="Mínimo 12 caracteres"
          minLength={12}
          required
        />
        <AuthField
          id="confirm"
          name="confirm"
          type="password"
          label="Repite la contraseña"
          icon={<LockIcon />}
          autoComplete="new-password"
          placeholder="Escríbela otra vez"
          required
        />
        <ErrorText>{error}</ErrorText>
        <AuthSubmit busy={busy}>{busy ? "Guardando…" : "Guardar contraseña"}</AuthSubmit>
      </form>
    </AuthLayout>
  );
}
